const YOTPO_WIDGET_SELECTOR = '.yotpo-widget-instance';
const YOTPO_DISTRIBUTION_BUTTON_SELECTOR = 'button[aria-pressed][aria-label]';
const initializedWidgets = new WeakSet();

/**
 * Adds Yotpo's visible rating/count string to the accessible name for strict
 * WCAG 2.5.3 scanners while preserving Yotpo's descriptive label first.
 *
 * @param {HTMLButtonElement} button
 */
function patchDistributionButton(button) {
  const rating = button.querySelector('.yotpo-star-distribution-index')?.textContent?.trim();
  const count = button.querySelector('.yotpo-star-distribution-value')?.textContent?.trim();
  const label = button.getAttribute('aria-label')?.trim();

  if (!rating || !count || !label || !/^\d+$/.test(rating) || !/^\d+$/.test(count)) return;

  const yotpoLabelPattern = new RegExp(`^${rating}\\s+stars?\\s+by\\s+${count}\\s+reviews?$`, 'i');

  if (!yotpoLabelPattern.test(label)) return;

  button.setAttribute('aria-label', `${label}; visible label ${rating} ${count}`);
}

/**
 * Patches matching controls within a newly rendered portion of a Yotpo widget.
 *
 * @param {Element} root
 */
function patchWidgetContent(root) {
  if (root.matches(YOTPO_DISTRIBUTION_BUTTON_SELECTOR)) {
    patchDistributionButton(/** @type {HTMLButtonElement} */ (root));
  }

  for (const button of root.querySelectorAll(YOTPO_DISTRIBUTION_BUTTON_SELECTOR)) {
    patchDistributionButton(/** @type {HTMLButtonElement} */ (button));
  }
}

/**
 * Observes one Yotpo instance because the app renders and replaces its controls asynchronously.
 *
 * @param {Element} widget
 */
function observeWidget(widget) {
  if (initializedWidgets.has(widget)) return;

  initializedWidgets.add(widget);
  patchWidgetContent(widget);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof Element) {
        patchWidgetContent(mutation.target);
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (node instanceof Element) patchWidgetContent(node);
      }
    }
  });

  observer.observe(widget, {
    attributes: true,
    attributeFilter: ['aria-label'],
    childList: true,
    subtree: true,
  });
}

/**
 * Initializes Yotpo instances in the document or a newly loaded theme-editor section.
 *
 * @param {ParentNode} root
 */
function initializeYotpoWidgets(root = document) {
  for (const widget of root.querySelectorAll(YOTPO_WIDGET_SELECTOR)) {
    observeWidget(widget);
  }
}

initializeYotpoWidgets();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initializeYotpoWidgets(event.target);
});
